import React, { useState, useCallback, useMemo } from 'react';
import { X, Search, MapPin, Building, User, Truck } from 'lucide-react';

const SelectionModals = ({ 
  showModals, 
  onCloseModal, 
  onSelect, 
  originsData, 
  destinationsData, 
  suppliersData, 
  usersData 
}) => {
  const [searchTerms, setSearchTerms] = useState({
    origins: '',
    destinations: '',
    suppliers: '',
    consignees: '',
    ff: '',
    seller: ''
  });

  const [activeDetailIndex, setActiveDetailIndex] = useState(null);

  // Función para limpiar término de búsqueda (memoizada)
  const clearSearch = useCallback((modalType) => {
    setSearchTerms(prev => ({ ...prev, [modalType]: '' }));
  }, []);

  // Función para manejar selección (memoizada)
  const handleSelect = useCallback((modalType, item) => {
    onSelect(modalType, item, activeDetailIndex);

    // Solo cerrar si NO es suppliers
    if (modalType !== 'suppliers') {
      onCloseModal(modalType);
      clearSearch(modalType);
      setActiveDetailIndex(null);
    }
    // Para suppliers, solo limpiar búsqueda pero mantener modal abierto
    else {
      clearSearch(modalType);
    }
  }, [onSelect, onCloseModal, activeDetailIndex, clearSearch]);

  // Función para manejar cierre (memoizada)
  const handleClose = useCallback((modalType) => {
    onCloseModal(modalType);
    clearSearch(modalType);
    setActiveDetailIndex(null);
  }, [onCloseModal, clearSearch]);

  // Función para actualizar searchTerm específico (memoizada)
  const updateSearchTerm = useCallback((modalType, value) => {
    setSearchTerms(prev => ({ ...prev, [modalType]: value }));
  }, []);

  // Función para actualizar índice activo cuando se abren modales de detalles
  React.useEffect(() => {
    if (showModals.consignees || showModals.suppliers || showModals.ff || showModals.seller) {
      // El índice se pasaría como parámetro adicional, por ahora usamos 0
      setActiveDetailIndex(0);
    }
  }, [showModals.consignees, showModals.suppliers, showModals.ff, showModals.seller]);

  // Memoizar listas filtradas para evitar recálculos innecesarios
  const filteredOrigins = useMemo(() => {
    return originsData?.origins?.filter(origin =>
      origin.origin_name.toLowerCase().includes(searchTerms.origins.toLowerCase())
    ) || [];
  }, [originsData?.origins, searchTerms.origins]);

  const filteredDestinations = useMemo(() => {
    return destinationsData?.destinations?.filter(destination =>
      destination.destination_name.toLowerCase().includes(searchTerms.destinations.toLowerCase())
    ) || [];
  }, [destinationsData?.destinations, searchTerms.destinations]);

  const filteredSuppliers = useMemo(() => {
    return suppliersData?.suppliers?.filter(supplier =>
      supplier.supplier_name.toLowerCase().includes(searchTerms.suppliers.toLowerCase())
    ) || [];
  }, [suppliersData?.suppliers, searchTerms.suppliers]);

  const filteredConsignees = useMemo(() => {
    return usersData?.users?.filter(user =>
      // Usuarios ACTIVOS con rol "Cliente" o "Transportista"
      user.is_active &&
      ['Cliente', 'Transportista'].includes(user.role?.role_name) &&
      (user.full_name.toLowerCase().includes(searchTerms.consignees.toLowerCase()) ||
       user.email.toLowerCase().includes(searchTerms.consignees.toLowerCase()))
    ) || [];
  }, [usersData?.users, searchTerms.consignees]);

  const filteredFF = useMemo(() => {
    return usersData?.users?.filter(user =>
      // Solo usuarios ACTIVOS con rol "Transportista"
      user.is_active &&
      user.role?.role_name === 'Transportista' &&
      (user.full_name.toLowerCase().includes(searchTerms.ff.toLowerCase()) ||
       user.email.toLowerCase().includes(searchTerms.ff.toLowerCase()))
    ) || [];
  }, [usersData?.users, searchTerms.ff]);

  const filteredSeller = useMemo(() => {
    return usersData?.users?.filter(user =>
      // TODOS los usuarios ACTIVOS del sistema
      user.is_active &&
      (user.full_name.toLowerCase().includes(searchTerms.seller.toLowerCase()) ||
       user.email.toLowerCase().includes(searchTerms.seller.toLowerCase()))
    ) || [];
  }, [usersData?.users, searchTerms.seller]);

  // Modal para Origins (memoizado)
  const OriginsModal = React.memo(() => (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Seleccionar Origen</h3>
            </div>
            <button
              onClick={() => handleClose('origins')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar origen..."
                value={searchTerms.origins}
                onChange={(e) => updateSearchTerm('origins', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {filteredOrigins.length > 0 ? (
                filteredOrigins.map((origin) => (
                  <button
                    key={origin.id}
                    onClick={() => handleSelect('origins', origin)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{origin.origin_name}</div>
                    <div className="text-sm text-gray-500">{origin.origin_code}</div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.origins ? 'No se encontraron resultados' : 'Cargando...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  ));

  // Modal para Destinations
  const DestinationsModal = () => {

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold">Seleccionar Destino</h3>
            </div>
            <button
              onClick={() => handleClose('destinations')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar destino..."
                value={searchTerms.destinations}
                onChange={(e) => updateSearchTerm('destinations', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                autoFocus
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {filteredDestinations.length > 0 ? (
                filteredDestinations.map((destination) => (
                  <button
                    key={destination.id}
                    onClick={() => handleSelect('destinations', destination)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{destination.destination_name}</div>
                    <div className="text-sm text-gray-500">{destination.destination_code}</div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.destinations ? 'No se encontraron resultados' : 'Cargando...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para Suppliers
  const SuppliersModal = () => {

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <Building className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold">Seleccionar Proveedores</h3>
            </div>
            <button
              onClick={() => handleClose('suppliers')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nota informativa */}
          <div className="px-4 pt-3 pb-2">
            <p className="text-sm text-gray-600">
              Haz click en cada proveedor para agregarlo. El modal permanecerá abierto.
            </p>
          </div>

          <div className="p-4 pt-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={searchTerms.suppliers}
                onChange={(e) => updateSearchTerm('suppliers', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                autoFocus
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {filteredSuppliers.length > 0 ? (
                filteredSuppliers.map((supplier) => (
                  <button
                    key={supplier.id}
                    onClick={() => handleSelect('suppliers', supplier)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{supplier.supplier_name}</div>
                    <div className="text-sm text-gray-500">{supplier.supplier_code}</div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.suppliers ? 'No se encontraron resultados' : 'Cargando...'}
                </div>
              )}
            </div>
          </div>

          {/* Footer con botón cerrar */}
          <div className="border-t p-4">
            <button
              onClick={() => handleClose('suppliers')}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal para Consignees
  const ConsigneesModal = () => {

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-indigo-600" />
              <div>
                <h3 className="text-lg font-semibold">Seleccionar Consignatario</h3>
                <p className="text-sm text-gray-500">Usuarios con rol Cliente o Transportista</p>
              </div>
            </div>
            <button
              onClick={() => handleClose('consignees')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar consignatario..."
                value={searchTerms.consignees}
                onChange={(e) => updateSearchTerm('consignees', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {/* Opción para digitar manualmente */}
              <button
                onClick={() => handleSelect('consignees', { id: 'manual' })}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors border-b border-gray-200 mb-2"
              >
                <div className="font-medium text-indigo-600">Digitar consignatarios manualmente</div>
                <div className="text-sm text-gray-400">Escribir nombres en campo de texto</div>
              </button>

              {filteredConsignees.length > 0 ? (
                filteredConsignees.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelect('consignees', user)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className={`text-xs font-medium ${
                      user.role?.role_name === 'Transportista'
                        ? 'text-orange-600'
                        : user.role?.role_name === 'Cliente'
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}>
                      {user.role?.role_name}
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.consignees ? 'No se encontraron usuarios con rol Cliente' : 'Cargando usuarios con rol Cliente...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para FF
  const FFModal = () => {

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <Truck className="w-5 h-5 text-orange-600" />
              <div>
                <h3 className="text-lg font-semibold">Seleccionar FF (Freight Forwarder)</h3>
                <p className="text-sm text-gray-500">Solo usuarios con rol Transportista</p>
              </div>
            </div>
            <button
              onClick={() => handleClose('ff')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar FF..."
                value={searchTerms.ff}
                onChange={(e) => updateSearchTerm('ff', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                autoFocus
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              {/* Opción para limpiar selección */}
              <button
                onClick={() => handleSelect('ff', null)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors border-b border-gray-200 mb-2"
              >
                <div className="font-medium text-gray-500">Sin FF</div>
                <div className="text-sm text-gray-400">Limpiar selección</div>
              </button>
              
              {filteredFF.length > 0 ? (
                filteredFF.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelect('ff', user)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-400">{user.role?.role_name}</div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.ff ? 'No se encontraron usuarios con rol Transportista' : 'Cargando usuarios con rol Transportista...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para Seller (Vendedor)
  const SellerModal = () => {

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-purple-600" />
              <div>
                <h3 className="text-lg font-semibold">Seleccionar Vendedor</h3>
                <p className="text-sm text-gray-500">Todos los usuarios del sistema</p>
              </div>
            </div>
            <button
              onClick={() => handleClose('seller')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar vendedor..."
                value={searchTerms.seller}
                onChange={(e) => updateSearchTerm('seller', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                autoFocus
              />
            </div>

            <div className="max-h-60 overflow-y-auto">
              {/* Opción para limpiar selección */}
              <button
                onClick={() => handleSelect('seller', null)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors border-b border-gray-200 mb-2"
              >
                <div className="font-medium text-gray-500">Sin vendedor</div>
                <div className="text-sm text-gray-400">Limpiar selección (opcional)</div>
              </button>

              {filteredSeller.length > 0 ? (
                filteredSeller.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelect('seller', user)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-400">{user.role?.role_name}</div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerms.seller ? 'No se encontraron usuarios' : 'Cargando usuarios...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showModals.origins && <OriginsModal />}
      {showModals.destinations && <DestinationsModal />}
      {showModals.suppliers && <SuppliersModal />}
      {showModals.consignees && <ConsigneesModal />}
      {showModals.ff && <FFModal />}
      {showModals.seller && <SellerModal />}
    </>
  );
};

export default SelectionModals;